<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n();

const router = useRouter();

</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />
    <main id="main-content" class="flex-1 p-6">
      <div id="dashboard-header" class="mb-8">
        <h1 class="text-3xl font-bold text-accent mb-2">
          {{ $t('developer.dashboard_page.title') }}
        </h1>
        <p class="text-gray-600">
          {{ $t('developer.dashboard_page.subtitle') }}
        </p>
      </div>

      <section id="quick-stats" class="mb-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">{{ $t('developer.dashboard_page.stats.registered_projects') }}</p>
                <p class="text-3xl font-bold text-accent">12</p>
              </div>
              <div
                class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-project-diagram text-primary text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">{{ $t('developer.dashboard_page.stats.pending_reviews') }}</p>
                <p class="text-3xl font-bold text-secondary">3</p>
              </div>
              <div
                class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-clock text-secondary text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">{{ $t('developer.dashboard_page.stats.issued_credits') }}</p>
                <p class="text-3xl font-bold text-primary">24,567</p>
              </div>
              <div
                class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-leaf text-primary text-xl"></i>
              </div>
            </div>
          </div>

          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">{{ $t('developer.dashboard_page.stats.total_revenue') }}</p>
                <p class="text-3xl font-bold text-accent">$1.2M</p>
              </div>
              <div
                class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-dollar-sign text-accent text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="action-buttons" class="mb-8">
        <div class="bg-white border border-gray-200 rounded-lg p-6">
          <h2 class="text-xl font-semibold text-accent mb-6">{{ $t('developer.dashboard_page.quick_actions.title') }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              @click="router.push('/developer/project/submit-pcn')"
              class="flex items-center space-x-3 p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
            >
              <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i class="fas fa-file-signature text-green-600"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">Submit PCN</div>
                <div class="text-sm text-gray-600">Project Concept Note — first 6.4 step</div>
              </div>
            </button>

            <button
              @click="router.push('/developer/project/submit-pin')"
              class="flex items-center space-x-3 p-4 border border-primary/20 rounded-lg hover:bg-primary/5 transition-colors"
            >
              <div
                class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-lightbulb text-primary"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.submit_pin') }}</div>
                <div class="text-sm text-gray-600">{{ $t('developer.dashboard_page.quick_actions.submit_pin_desc') }}</div>
              </div>
            </button>

            <button
              @click="router.push('/developer/project/submit-pdd')"
              class="flex items-center space-x-3 p-4 border border-secondary/20 rounded-lg hover:bg-secondary/5 transition-colors"
            >
              <div
                class="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-file-alt text-secondary"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.submit_pdd') }}</div>
                <div class="text-sm text-gray-600">{{ $t('developer.dashboard_page.quick_actions.submit_pdd_desc') }}</div>
              </div>
            </button>

            <button
              @click="router.push('/developer/upload-reports')"
              class="flex items-center space-x-3 p-4 border border-accent/20 rounded-lg hover:bg-accent/5 transition-colors"
            >
              <div
                class="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-upload text-accent"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.upload_reports') }}</div>
                <div class="text-sm text-gray-600">
                  {{ $t('developer.dashboard_page.quick_actions.upload_reports_desc') }}
                </div>
              </div>
            </button>

            <button
              @click="router.push('/developer/request-credits')"
              class="flex items-center space-x-3 p-4 border border-primary/20 rounded-lg hover:bg-primary/5 transition-colors"
            >
              <div
                class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-certificate text-primary"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.request_credits') }}</div>
                <div class="text-sm text-gray-600">{{ $t('developer.dashboard_page.quick_actions.request_credits_desc') }}</div>
              </div>
            </button>

            <button
              @click="router.push('/developer/legacy-registration')"
              class="flex items-center space-x-3 p-4 border border-amber-500/20 rounded-lg hover:bg-amber-50 transition-colors"
            >
              <div
                class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-archive text-amber-600"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.legacy_registration') }}</div>
                <div class="text-sm text-gray-600">{{ $t('developer.dashboard_page.quick_actions.legacy_registration_desc') }}</div>
              </div>
            </button>

            <button
              @click="router.push('/developer/browse-registry')"
              class="flex items-center space-x-3 p-4 border border-blue-500/20 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <div
                class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"
              >
                <i class="fas fa-search text-blue-600"></i>
              </div>
              <div class="text-left">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.quick_actions.browse_registry') }}</div>
                <div class="text-sm text-gray-600">{{ $t('developer.dashboard_page.quick_actions.browse_registry_desc') }}</div>
              </div>
            </button>
          </div>
        </div>
      </section>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section
          id="recent-projects"
          class="bg-white border border-gray-200 rounded-lg p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-accent">{{ $t('developer.dashboard_page.recent_projects.title') }}</h2>
            <button
              class="text-primary hover:text-primary/80 text-sm font-medium"
            >
              {{ $t('developer.dashboard_page.recent_projects.view_all') }}
            </button>
          </div>
          <div class="space-y-4">
            <div
              class="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-wind text-primary text-sm"></i>
                </div>
                <div>
                  <div class="font-medium text-accent">Wind Farm Alpha</div>
                  <div class="text-sm text-gray-600">PIN Submitted</div>
                </div>
              </div>
              <span
                class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                >Under Review</span
              >
            </div>

            <div
              class="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-solar-panel text-primary text-sm"></i>
                </div>
                <div>
                  <div class="font-medium text-accent">Solar Park Beta</div>
                  <div class="text-sm text-gray-600">PDD Approved</div>
                </div>
              </div>
              <span
                class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                >Active</span
              >
            </div>

            <div
              class="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-tree text-primary text-sm"></i>
                </div>
                <div>
                  <div class="font-medium text-accent">Forest Conservation</div>
                  <div class="text-sm text-gray-600">Credits Issued</div>
                </div>
              </div>
              <span
                class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >Completed</span
              >
            </div>
          </div>
        </section>

        <section
          id="pending-tasks"
          class="bg-white border border-gray-200 rounded-lg p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-accent">{{ $t('developer.dashboard_page.pending_tasks.title') }}</h2>
            <span class="px-2 py-1 bg-error/10 text-error text-xs rounded-full"
              >3 {{ $t('developer.dashboard_page.pending_tasks.urgent') }}</span
            >
          </div>
          <div class="space-y-4">
            <div
              class="flex items-start space-x-3 p-3 border border-error/20 rounded-lg bg-error/5"
            >
              <i class="fas fa-exclamation-triangle text-error mt-1"></i>
              <div class="flex-1">
                <div class="font-medium text-accent">
                  {{ $t('developer.dashboard_page.pending_tasks.doe_selection_required') }}
                </div>
                <div class="text-sm text-gray-600 mb-2">
                  {{ $t('developer.dashboard_page.pending_tasks.doe_selection_desc') }}
                </div>
                <div class="text-xs text-error">Due: Dec 25, 2024</div>
              </div>
            </div>

            <div
              class="flex items-start space-x-3 p-3 border border-amber-200 rounded-lg bg-amber-50"
            >
              <i class="fas fa-clock text-amber-600 mt-1"></i>
              <div class="flex-1">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.pending_tasks.monitoring_report_due') }}</div>
                <div class="text-sm text-gray-600 mb-2">
                  {{ $t('developer.dashboard_page.pending_tasks.monitoring_report_desc') }}
                </div>
                <div class="text-xs text-amber-600">Due: Dec 31, 2024</div>
              </div>
            </div>

            <div
              class="flex items-start space-x-3 p-3 border border-blue-200 rounded-lg bg-blue-50"
            >
              <i class="fas fa-info-circle text-blue-600 mt-1"></i>
              <div class="flex-1">
                <div class="font-medium text-accent">{{ $t('developer.dashboard_page.pending_tasks.payment_pending') }}</div>
                <div class="text-sm text-gray-600 mb-2">
                  {{ $t('developer.dashboard_page.pending_tasks.payment_pending_desc') }}
                </div>
                <div class="text-xs text-blue-600">Invoice: INV-2024-078</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section
        id="notifications"
        class="mt-6 bg-white border border-gray-200 rounded-lg p-6"
      >
        <h2 class="text-xl font-semibold text-accent mb-4">
          {{ $t('developer.dashboard_page.notifications.title') }}
        </h2>
        <div class="space-y-3">
          <div
            class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
          >
            <div class="w-2 h-2 bg-primary rounded-full"></div>
            <div class="flex-1">
              <div class="text-sm font-medium text-accent">
                {{ $t('developer.dashboard_page.notifications.pin_review_completed') }}
              </div>
              <div class="text-xs text-gray-600">
                {{ $t('developer.dashboard_page.notifications.pin_review_desc') }}
              </div>
            </div>
            <div class="text-xs text-gray-500">{{ $t('developer.dashboard_page.notifications.time_2_hours_ago') }}</div>
          </div>

          <div
            class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
          >
            <div class="w-2 h-2 bg-secondary rounded-full"></div>
            <div class="flex-1">
              <div class="text-sm font-medium text-accent">{{ $t('developer.dashboard_page.notifications.doe_assignment') }}</div>
              <div class="text-xs text-gray-600">
                {{ $t('developer.dashboard_page.notifications.doe_assignment_desc') }}
              </div>
            </div>
            <div class="text-xs text-gray-500">{{ $t('developer.dashboard_page.notifications.time_1_day_ago') }}</div>
          </div>

          <div
            class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
          >
            <div class="w-2 h-2 bg-gray-400 rounded-full"></div>
            <div class="flex-1">
              <div class="text-sm font-medium text-accent">
                {{ $t('developer.dashboard_page.notifications.system_maintenance') }}
              </div>
              <div class="text-xs text-gray-600">
                {{ $t('developer.dashboard_page.notifications.system_maintenance_desc') }}
              </div>
            </div>
            <div class="text-xs text-gray-500">{{ $t('developer.dashboard_page.notifications.time_3_days_ago') }}</div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
